import { isDynamicValue, stringToJS } from './dynamicBindings';
import { isServerHardenedEval, sanitizeEvalResult } from './evaluateExpression';
import { createComplexExpressionsEvaluator } from './indirectEval';
import { newDefaultUpdater, resolveHardenedPathExpression } from './recursiveUpdate';

/**
 * Bulk evaluation for objects where all values are strings
 * Uses createComplexExpressionsEvaluator for better performance when evaluating multiple expressions
 */
export const getBulkComputedData = <T extends Record<string, string>>(
  data: T,
  context: Record<string, unknown>,
  shouldEvaluateResult?: (result: string) => boolean,
): Record<keyof T, unknown> => {
  const complexExpressionsEvaluator = createComplexExpressionsEvaluator(context);

  const result = bulkOptimisedEvaluateExpression({
    data,
    context,
    shouldEvaluateResult,
    complexExpressionsEvaluator,
  });
  return result;
};

/**
 * Bulk version of optimisedEvaluateExpression that processes multiple expressions at once
 * Follows the exact steps from evaluateExpression.ts:
 * 1.1: Check for dynamic expression and drill down more
 * 1.2: Convert to JS
 * 1.3: Call defaultUpdater on each expression
 * 1.4: Drill down more and skip keys that have undefined
 * 1.5: Now we have some object with keys and their values as response of resolvedStringToEvaluate
 * 1.6: Hit bulk evaluator which will give us some values in object (some must be fully evaluated, some might not)
 * 1.7: Check for all values that need re-evaluation and call step 1 function again
 */
export function bulkOptimisedEvaluateExpression<T extends Record<string, string>>({
  data,
  context,
  shouldEvaluateResult = isDynamicValue,
  complexExpressionsEvaluator,
}: {
  data: T;
  context: Record<string, unknown>;
  shouldEvaluateResult?: (result: string) => boolean;
  complexExpressionsEvaluator: (expressions: Record<string, string>) => Record<string, unknown>;
}): Record<keyof T, unknown> {
  // Step 1.1: Check for dynamic expression and drill down more
  // Step 1.2: Convert to JS
  // Step 1.3: Call defaultUpdater on each expression
  // Step 1.4: Drill down more and skip keys that have undefined
  // Step 1.5: Now we have some object with keys and their values as response of resolvedStringToEvaluate
  const dynamicExpressions: Record<string, string> = {};
  const reEvaluateExpressions: Record<string, string> = {};
  const staticValues: Record<string, unknown> = {};

  for (const key in data) {
    const expression = data[key];

    // Step 1.1: Check for dynamic expression and drill down more
    if (!isDynamicValue(expression)) {
      staticValues[key] = expression;
      continue;
    }

    // UA-004: under the server nested-eval flag, a whole-string path is read, never compiled.
    if (isServerHardenedEval(shouldEvaluateResult)) {
      const hardened = resolveHardenedPathExpression(expression, context);
      if (hardened.handled) {
        staticValues[key] = hardened.value;
        continue;
      }
    }

    // Step 1.2: Convert to JS
    const toBeResolvedJS = stringToJS(expression);

    // Step 1.3: Call defaultUpdater on each expression
    const { value: resolvedStringToEvaluate, isHandledByGetter } = newDefaultUpdater(
      context,
      toBeResolvedJS,
      shouldEvaluateResult,
    );

    // Step 1.4: Drill down more and skip keys that have undefined
    if (resolvedStringToEvaluate === undefined) {
      continue;
    }

    // Step 1.5: Now we have some object with keys and their values as response of resolvedStringToEvaluate
    if (typeof resolvedStringToEvaluate === 'string') {
      if (isHandledByGetter) {
        reEvaluateExpressions[key] = resolvedStringToEvaluate;
      } else {
        dynamicExpressions[key] = resolvedStringToEvaluate;
      }
    } else {
      staticValues[key] = resolvedStringToEvaluate;
    }
  }

  let reEvaluatedExpressions: Record<string, unknown> = {};
  let hasReEvaluateExpressions = false;
  for (const _ in reEvaluateExpressions) {
    hasReEvaluateExpressions = true;
    break;
  }

  if (hasReEvaluateExpressions) {
    reEvaluatedExpressions = bulkOptimisedEvaluateExpression({
      data: reEvaluateExpressions,
      context,
      shouldEvaluateResult,
      complexExpressionsEvaluator,
    });
  }

  // Step 1.6: Hit bulk evaluator which will give us some values in object (some must be fully evaluated, some might not)
  let evaluatedResults: Record<string, unknown> = {};
  let hasDynamicExpressions = false;
  for (const _ in dynamicExpressions) {
    hasDynamicExpressions = true;
    break;
  }

  if (hasDynamicExpressions) {
    evaluatedResults = bulkOptimisedEvaluateResult({
      expressions: dynamicExpressions,
      context,
      shouldEvaluateResult,
      complexExpressionsEvaluator,
    });
  }

  // Combine static and evaluated results - maintain stable object shape
  const result = { ...staticValues, ...evaluatedResults, ...reEvaluatedExpressions } as Record<
    keyof T,
    unknown
  >;
  return result;
}

/**
 * Bulk version of optimisedEvaluateResult that handles iterative re-evaluation
 * Follows the same pattern as optimisedEvaluateResult from evaluateExpression.ts
 */
function bulkOptimisedEvaluateResult({
  expressions,
  context,
  shouldEvaluateResult,
  complexExpressionsEvaluator,
}: {
  expressions: Record<string, string>;
  context: Record<string, unknown>;
  shouldEvaluateResult: (result: string) => boolean;
  complexExpressionsEvaluator: (expressions: Record<string, string>) => Record<string, unknown>;
}): Record<string, unknown> {
  try {
    // Step 1.6: Hit bulk evaluator which will give us some values in object (some must be fully evaluated, some might not)
    const results = complexExpressionsEvaluator(expressions);

    // Step 1.7: Check for all values that need re-evaluation and call step 1 function again
    const needsReEvaluation: Record<string, string> = {};
    const finalResults: Record<string, unknown> = {};

    for (const key in results) {
      const result = results[key];
      if (typeof result === 'string' && shouldEvaluateResult(result)) {
        needsReEvaluation[key] = result;
      } else {
        finalResults[key] = sanitizeEvalResult(result);
      }
    }

    let hasReEvaluation = false;
    for (const _ in needsReEvaluation) {
      hasReEvaluation = true;
      break;
    }

    if (hasReEvaluation) {
      const reEvaluatedResults = bulkOptimisedEvaluateExpression({
        data: needsReEvaluation,
        context,
        shouldEvaluateResult,
        complexExpressionsEvaluator,
      });

      for (const key in reEvaluatedResults) {
        finalResults[key] = reEvaluatedResults[key];
      }
      return finalResults;
    }

    return finalResults;
  } catch (e) {
    // console.error(e);
    return {};
  }
}
